import { useState } from 'react';
import { Download, Copy, Check, Loader2, ImageOff } from 'lucide-react';
import { copyToClipboard } from '../storyboardApi';

// One MOHAI Agent Asset: brand-new conversation image with download + paste-ready snippet.
export const AgentAssetCard = ({ asset, index }) => {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    if (!asset.image_url || downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(asset.image_url);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mohai-agent-asset-${String(index).padStart(2, '0')}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      window.open(asset.image_url, '_blank');
    }
    setDownloading(false);
  };

  const copySnippet = async () => {
    await copyToClipboard(asset.master_prompt_snippet || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="rounded-xl border border-gray-200/60 bg-white/50 p-3" data-testid={`agent-asset-${index}`}>
      <div className="flex gap-3 items-start">
        <div className="shrink-0 space-y-1.5">
          {asset.image_url ? (
            <img src={asset.image_url} alt={asset.title} className="w-28 h-20 rounded-lg object-cover border border-gray-200/60" data-testid={`agent-asset-${index}-preview`} />
          ) : (
            <div className="w-28 h-20 rounded-lg bg-gray-100 flex items-center justify-center">
              <ImageOff className="w-4 h-4 text-gray-300" />
            </div>
          )}
          <button
            data-testid={`agent-asset-${index}-download`}
            onClick={download}
            disabled={!asset.image_url || downloading}
            className="w-28 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium bg-gray-900 text-white hover:bg-gray-800 transition-all disabled:opacity-40"
          >
            {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            <span>Download</span>
          </button>
        </div>
        <div className="text-xs text-gray-600 leading-relaxed min-w-0 flex-1">
          <p className="font-medium text-gray-900">{index}. {asset.title}</p>
          <p className="mt-1"><span className="text-gray-400">Objective:</span> {asset.objective}</p>
          <p><span className="text-gray-400">When to show:</span> {asset.when_to_show}</p>
          <p><span className="text-gray-400">Trigger:</span> "{asset.trigger_message}"</p>
        </div>
      </div>
      {asset.master_prompt_snippet && (
        <div className="mt-2.5 rounded-lg bg-gray-50/80 border border-gray-200/50 p-2.5">
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Paste into Master Prompt</p>
            <button
              data-testid={`agent-asset-${index}-copy-snippet`}
              onClick={copySnippet}
              className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-white border border-gray-200/60 text-gray-600 hover:text-gray-900 transition-all"
            >
              {copied ? <Check className="w-2.5 h-2.5 text-green-600" /> : <Copy className="w-2.5 h-2.5" />}
              <span>{copied ? 'Copied' : 'Copy'}</span>
            </button>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">{asset.master_prompt_snippet}</p>
        </div>
      )}
    </div>
  );
};
